/**
 * Field Map — the dashboard panels around the field table.
 *
 * Each takes the server's payload (or a slice of it) and what the page needs to
 * hear back: a filter the reader chose, or a field they want opened. None keeps
 * a number of its own; every figure is a count the model derived from `fields[]`.
 */

import { useState } from 'react'
import clsx from 'clsx'
import { CheckCircle2 } from 'lucide-react'

import type { FieldMap, FieldMapOwner } from '@/services/loans'
import { DossierPanel } from './ui'
import {
  BUCKETS,
  GROUPS,
  OWNER_ASK,
  OWNER_BUCKET,
  pctText,
  stamp,
  type BucketCounts,
  type PageBar,
  type StatusFilter,
  type UnlockEntry,
} from './fieldMapModel'
import { Chip, Curve, Legend, StackBar, Swatch, Tile } from './FieldMapParts'

const HATCH = 'repeating-linear-gradient(45deg, rgba(0,0,0,.55) 0 2px, transparent 2px 5px)'
const OWNERS: readonly FieldMapOwner[] = ['broker', 'inputs', 'reviewer', 'us', 'desk']

/** The seven tiles: three scores, then the four reasons that matter most. */
export function ScoreTiles({ data, counts }: { data: FieldMap; counts: BucketCounts }) {
  const { score } = data
  const missing = score.gate.missing
  return (
    <section aria-label="Scores" className="grid shrink-0 grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-7 max-xl:[&>*:last-child]:col-span-2">
      <Tile
        label="On the dossier"
        value={`${score.overall.populated} / ${score.overall.total}`}
        fraction={score.overall.pct}
        aside={pctText(score.overall.pct)}
        accent={BUCKETS.shown.color}
        title="Every catalog field the dossier shows, counted once: the deal's own value or the intake record's."
      />
      <Tile
        label="Package-fillable"
        value={`${score.package.populated} / ${score.package.total}`}
        fraction={score.package.pct}
        aside={pctText(score.package.pct)}
        accent={BUCKETS.shown.color}
        title="Fields a package can fill: leaves out fields the desk sets and fields closed as inapplicable to this asset."
      />
      <Tile
        label="Gate fields"
        value={`${score.gate.populated} / ${score.gate.total}`}
        fraction={score.gate.pct}
        aside={missing.length ? `Missing: ${missing.join(', ')}` : 'complete'}
        accent={BUCKETS.absent.color}
        tone={score.gate.populated < score.gate.total ? 'text-amber-300' : undefined}
        title={missing.length ? `Missing: ${missing.join(', ')}` : 'Every field required at intake or pipeline-critical is held.'}
      />
      <Tile label="Not in the package" value={String(counts.absent)} aside="Ask the broker" accent={BUCKETS.absent.color} title={BUCKETS.absent.hint} />
      <Tile label="Derivable" value={String(counts.derivable)} aside="Once inputs arrive" accent={BUCKETS.derivable.color} title={BUCKETS.derivable.hint} />
      <Tile label="Waiting on a person" value={String(counts.review)} aside="A reviewer decides" accent={BUCKETS.review.color} title={BUCKETS.review.hint} />
      <Tile
        label="Engine misses"
        value={String(score.engineMisses)}
        aside="Stated, not held"
        accent={BUCKETS.miss.color}
        tone={score.engineMisses > 0 ? 'text-rose-400' : undefined}
        title="Fields a source states and the dossier does not hold, or that could not be searched: the engine's own defects."
      />
    </section>
  )
}

function PageRow({ bar, all = false, active, onPage }: { bar: PageBar; all?: boolean; active: boolean; onPage?: () => void }) {
  return (
    <li
      data-page-bar={bar.page}
      className={clsx(
        'grid min-h-0 flex-1 grid-cols-[5.25rem_minmax(0,1fr)_6.5rem] items-center gap-2',
        all && 'border-t border-white/[0.07] pt-1',
      )}
    >
      {onPage ? (
        <button
          type="button"
          aria-pressed={active}
          title={`Show only ${bar.page} fields in the table`}
          onClick={onPage}
          className={clsx(
            'truncate text-left text-[11px] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300',
            active ? 'text-amber-200' : 'text-stone-300 hover:text-stone-100',
          )}
        >
          {bar.page}
        </button>
      ) : (
        <span className="truncate text-[11px] font-medium text-stone-100">{bar.page}</span>
      )}
      <StackBar counts={bar.counts} total={bar.total} name={bar.page} />
      <span className="flex items-baseline justify-end gap-1.5 whitespace-nowrap tabular-nums">
        <span className="text-[11px] font-medium text-stone-100">{bar.total ? pctText(bar.pct) : '—'}</span>
        <span className="text-[10px] text-stone-500">{bar.populated} / {bar.total}</span>
      </span>
    </li>
  )
}

/** One stacked bar per dossier page, closed by a bar for every field. The legend filters the table. */
export function PagesPanel({
  pages,
  all,
  status,
  onStatus,
  pageFilter,
  onPage,
}: {
  pages: PageBar[]
  all: PageBar
  status: StatusFilter
  onStatus: (next: StatusFilter) => void
  pageFilter: string
  onPage: (next: string) => void
}) {
  return (
    <DossierPanel title="Why fields are blank, page by page" dense>
      <div className="flex h-full min-h-0 flex-col gap-2 p-1">
        <Legend counts={all.counts} active={status} onPick={onStatus} />
        <ul className="flex min-h-0 flex-1 flex-col gap-1" aria-label="Fields by dossier page">
          {pages.map((bar) => (
            <PageRow key={bar.page} bar={bar} active={pageFilter === bar.page} onPage={() => onPage(pageFilter === bar.page ? '' : bar.page)} />
          ))}
          <PageRow bar={all} all active={false} />
        </ul>
      </div>
    </DossierPanel>
  )
}

function UnlockRow({ entry, max, onOpen }: { entry: UnlockEntry; max: number; onOpen: () => void }) {
  const bucket = BUCKETS[entry.owner ? OWNER_BUCKET[entry.owner] : 'absent']
  const names = entry.targets.map((t) => (t.alone ? t.label : `${t.label} (also needs ${t.alsoNeeds.join(', ')})`)).join('; ')
  const noun = entry.total === 1 ? 'field' : 'fields'
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        title={`${entry.label} unlocks ${entry.total} calculated ${noun}, ${entry.alone} on its own: ${names}`}
        aria-label={`${entry.label}: unlocks ${entry.total} calculated ${noun}, ${entry.alone} on its own`}
        className="grid w-full grid-cols-[minmax(0,9.5rem)_minmax(0,1fr)_1.5rem] items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300"
      >
        <span className="flex min-w-0 items-center gap-1 text-[11px] text-stone-200">
          <span className="truncate">{entry.label}</span>
          {entry.gate ? <span className="font-mono text-[8px] uppercase tracking-wider text-amber-300" title="Gate field">G</span> : null}
        </span>
        <span className="flex h-3.5 overflow-hidden rounded-sm bg-white/[0.05]" aria-hidden>
          <i className="block h-full" style={{ width: `${(100 * entry.alone) / max}%`, backgroundColor: bucket.color }} />
          <i
            className="block h-full"
            style={{ width: `${(100 * (entry.total - entry.alone)) / max}%`, backgroundColor: bucket.color, backgroundImage: HATCH }}
          />
        </span>
        <span className="text-right text-[11px] font-semibold tabular-nums text-stone-100">{entry.total}</span>
      </button>
    </li>
  )
}

/**
 * The blank inputs ranked by the calculated fields they would fill. The colour
 * is who has to supply the input (the same colour as its worklist group);
 * solid is what it completes alone, hatched what needs other inputs too.
 */
export function UnlockPanel({ entries, onOpen }: { entries: UnlockEntry[]; onOpen: (field: string) => void }) {
  const max = Math.max(1, ...entries.map((u) => u.total))
  const owners = OWNERS.filter((o) => entries.some((u) => u.owner === o))
  return (
    <DossierPanel title="What to supply next" dense>
      {(ctx) => <div className="flex h-full min-h-0 flex-col gap-1.5 p-1">
        <ul className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-stone-500" aria-label="How to read the bars">
          {owners.map((o) => (
            <li key={o} className="inline-flex items-center gap-1"><Swatch bucket={OWNER_BUCKET[o]} />{OWNER_ASK[o]}</li>
          ))}
          <li className="inline-flex items-center gap-1" title="The input alone completes the calculated field">
            <i aria-hidden className="inline-block h-2 w-3 rounded-[1px] bg-stone-400" />Fills alone
          </li>
          <li className="inline-flex items-center gap-1" title="Other inputs are missing too">
            <i aria-hidden className="inline-block h-2 w-3 rounded-[1px] bg-stone-400" style={{ backgroundImage: HATCH }} />Needs others too
          </li>
        </ul>
        {entries.length === 0 ? (
          <p className="flex flex-1 items-center justify-center text-center text-[11px] text-stone-500">
            No blank field holds back a calculated one.
          </p>
        ) : (
          // The list scrolls inside a box that takes the panel's height without asking for any.
          <div className="relative min-h-[5rem] flex-1">
            <ul className="absolute inset-0 space-y-0.5 overflow-y-auto pr-1" aria-label="Inputs ranked by calculated fields unlocked">
              {entries.map((u) => (
                <UnlockRow key={u.field} entry={u} max={max} onOpen={() => { ctx.close(); onOpen(u.field) }} />
              ))}
            </ul>
          </div>
        )}
      </div>}
    </DossierPanel>
  )
}

/** The intake records that fed the deal, and how its fields were populated over time. */
export function ExtractionPanel({ data }: { data: FieldMap }) {
  const most = Math.max(1, ...data.extractions.map((x) => x.fieldsRead))
  return (
    <DossierPanel title="Extraction" dense>
      <div className="flex h-full min-h-0 flex-col gap-2 p-1">
        <section aria-label="Intake records" className="shrink-0">
          <h3 className="mb-1 font-mono text-[9px] font-normal uppercase tracking-wider text-stone-500">Intake records that fed this deal</h3>
          {data.extractions.length === 0 ? (
            <p className="text-[11px] text-stone-500">No intake record is attached: this deal was entered by hand.</p>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {data.extractions.map((e) => (
                <li key={e.id} className="py-0.5 text-[11px]">
                  <div className="flex items-center gap-2 text-stone-200">
                    <span className="truncate">{e.id}</span>
                    {e.isSourceRecord ? <Chip>Source record</Chip> : null}
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] tabular-nums text-stone-500">
                    <span>{stamp(e.receivedAt)}</span>
                    <span aria-hidden>·</span>
                    <span>{e.attachments} {e.attachments === 1 ? 'attachment' : 'attachments'}</span>
                    <span aria-hidden>·</span>
                    <span aria-hidden className="h-1 w-8 overflow-hidden rounded-full bg-white/[0.07]">
                      <span className="block h-full rounded-full bg-amber-300/80" style={{ width: `${(100 * e.fieldsRead) / most}%` }} />
                    </span>
                    <span className="text-stone-300">{e.fieldsRead} fields read</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section aria-label="Population over time" className="flex min-h-0 flex-1 flex-col">
          <h3 className="mb-1 font-mono text-[9px] font-normal uppercase tracking-wider text-stone-500">Fields populated, by recorded event</h3>
          {data.population.length === 0 ? (
            <p className="text-[11px] text-stone-500">No population event was recorded for this deal.</p>
          ) : (
            <Curve points={data.population} />
          )}
        </section>
      </div>
    </DossierPanel>
  )
}

const PILL = 'rounded border px-1.5 py-0.5 text-[10px] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300'
const PILL_ON = 'border-amber-300/60 bg-amber-300/10 text-amber-200'
const PILL_OFF = 'border-white/[0.08] text-stone-400 hover:text-stone-200'

/** The next asks, grouped by who has to move; a pill narrows to one group. */
export function WorklistPanel({ data, onOpen }: { data: FieldMap; onOpen: (field: string) => void }) {
  const [owner, setOwner] = useState<FieldMapOwner | ''>('')
  const asks = data.worklist.filter((w) => !owner || w.owner === owner)
  const groups = GROUPS.filter((g) => data.worklist.some((w) => w.owner === g.owner))
  return (
    <DossierPanel
      title="To complete"
      dense
      scroll={false}
      headerExtra={<span className="font-mono text-[10px] tabular-nums text-stone-500">{data.worklist.length} asks</span>}
    >
      {(ctx) => data.worklist.length === 0 ? (
        <div className="flex h-full items-center justify-center gap-2 p-3 text-center text-[12px] text-emerald-300/90">
          <CheckCircle2 size={14} className="shrink-0" aria-hidden /> Nothing is left to ask for: every field a package can fill is held or waiting on a person.
        </div>
      ) : (
        <div className="flex h-full min-h-0 flex-col gap-1.5">
          <div className="flex shrink-0 flex-wrap gap-1 px-1 pt-1" role="group" aria-label="Filter the asks by who has to move">
            <button type="button" aria-pressed={owner === ''} onClick={() => setOwner('')} className={clsx(PILL, owner === '' ? PILL_ON : PILL_OFF)}>
              All <span className="font-mono tabular-nums text-stone-500">{data.worklist.length}</span>
            </button>
            {groups.map((g) => (
              <button
                key={g.owner}
                type="button"
                aria-pressed={owner === g.owner}
                title={g.lead}
                onClick={() => setOwner(owner === g.owner ? '' : g.owner)}
                className={clsx(PILL, 'inline-flex items-center gap-1.5', owner === g.owner ? PILL_ON : PILL_OFF)}
              >
                <Swatch bucket={OWNER_BUCKET[g.owner]} />
                {g.title}
                <span className="font-mono tabular-nums text-stone-500">{data.worklist.filter((w) => w.owner === g.owner).length}</span>
              </button>
            ))}
          </div>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-1 pb-1">
            {GROUPS.map((g) => {
              const items = asks.filter((w) => w.owner === g.owner)
              if (!items.length) return null
              return (
                <section key={g.owner} aria-label={g.title}>
                  <h3 className="sticky top-0 z-[1] flex items-center gap-1.5 bg-stone-950/95 py-0.5 font-sans text-[11px] font-medium text-stone-200">
                    <Swatch bucket={OWNER_BUCKET[g.owner]} />
                    <span>{g.title}</span>
                    <span className="font-normal text-stone-500">({items.length})</span>
                    <span className="min-w-0 truncate text-[10px] font-normal text-stone-600" title={g.lead}>{g.lead}</span>
                  </h3>
                  <ul className="divide-y divide-white/[0.05]">
                    {items.map((w) => (
                      <li key={w.field}>
                        <button
                          type="button"
                          onClick={() => { ctx.close(); onOpen(w.field) }}
                          className="block w-full rounded px-1.5 py-1 text-left hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300"
                        >
                          <span className="flex flex-wrap items-center gap-1.5 text-[12px] text-stone-100">
                            {w.label}
                            <Chip>{w.page}</Chip>
                            {w.gate ? <Chip tone="amber">Gate</Chip> : null}
                          </span>
                          <span className="line-clamp-2 text-[11px] leading-snug text-stone-400" title={w.need}>{w.need}</span>
                          {w.blocks.length ? (
                            <span className="block truncate text-[10px] text-stone-500" title={w.blocks.join(', ')}>Holds back: {w.blocks.join(', ')}</span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )
            })}
            {data.worklist.length >= 60 ? (
              <p className="text-[10px] text-stone-600">Showing the 60 highest-priority asks; the field table lists the rest.</p>
            ) : null}
          </div>
        </div>
      )}
    </DossierPanel>
  )
}
